import { useState, useEffect, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { observer, inject } from 'mobx-react';
import ButtonWithLoading from '../form-fields/ButtonWithLoading';
import CloseOneIcon from '../svg/CloseOneIcon';
import { formatDateTime } from '@/utils/dateFormat';
const LoadCartModal = inject('wooCommerceStore', 'studioStore', 'generalSettingsStore')(observer(({
  isOpen,
  onClose,
  wooCommerceStore,
  studioStore,
  generalSettingsStore,
}) => {
  const t = useTranslations('Cart');
  const tg = useTranslations('General');
  const [savedCarts, setSavedCarts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingCartId, setLoadingCartId] = useState(null);
  const [error, setError] = useState('');
  const dateFormat = generalSettingsStore.dateFormat;

  const container = studioStore.settings.product_popup?.container;
  const productInfoBackgroundColor = studioStore.settings.product_popup?.product_info_background_color || 'white';
  const primaryColor = studioStore.settings.colors.primary_color;

  const fetchSavedCarts = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      const carts = await wooCommerceStore.getSavedCarts();
      setSavedCarts(carts);
    } catch (err) {
      setError(err.message || t('failed_to_load_carts'));
    } finally {
      setIsLoading(false);
    }
  }, [wooCommerceStore, t]);

  useEffect(() => {
    if (isOpen) {
      fetchSavedCarts();
    }
  }, [isOpen, fetchSavedCarts]);

  const handleLoadCart = async (cartId) => {
    setLoadingCartId(cartId);
    setError('');

    try {
      await wooCommerceStore.loadCart(cartId);
      onClose();
    } catch (err) {
      setError(err.message || t('failed_to_load_cart'));
    } finally {
      setLoadingCartId(null);
    }
  };

  if (!isOpen) return null;

  let classes = "fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md md:mx-auto max-h-[90vh] z-[60] overscroll-contain";
  const style = {
    backgroundColor: productInfoBackgroundColor,
  };

  if (container === "stamp") {
    classes = classes + " stamp-border";
    style.backgroundImage = `radial-gradient(8px, transparent 98%, ${productInfoBackgroundColor}),linear-gradient(${productInfoBackgroundColor} 0px, ${productInfoBackgroundColor} 0px)`;
    style.backgroundColor = '';
  } else {
    classes = classes + " rounded-lg shadow-lg p-4 bg-white";
  }

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="fixed inset-0 transition-opacity" aria-hidden="true">
        <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <div className={classes} style={style}>
        <button
          className="absolute top-4 md:-top-8 left-4 md:-left-10 rtl:right-4 rtl:md:-right-10 rtl:left-auto z-10"
          onClick={onClose}
        >
          <CloseOneIcon size="28px" stroke={primaryColor} />
        </button>

        <div className="max-h-[86vh] overflow-y-auto">
          <div className="p-3 md:p-6">
            <div className="px-4 md:px-8">
              <h2 className="text-2xl font-semibold mb-6 text-center" style={{color: primaryColor}}>
                {t('load_cart')}
              </h2>

              {error && (
                <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
                  {error}
                </div>
              )}

              {isLoading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-studio"></div>
                </div>
              ) : savedCarts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>{t('no_saved_carts')}</p>
                </div>
              ) : (
                <div className="max-h-80 overflow-y-auto">
                  <ul className="divide-y divide-gray-200">
                    {savedCarts.map((cart) => (
                      <li key={cart.id} className="py-3">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium">{cart.name}</p>
                            <p className="text-sm text-gray-500">
                              {formatDateTime(cart.created_at, dateFormat)} | {cart.items.length || 0} {t('items')}
                            </p>
                          </div>
                          <ButtonWithLoading
                            type="button"
                            onClick={() => handleLoadCart(cart.id)}
                            text={t('load')}
                            loading={loadingCartId === cart.id}
                            disabled={loadingCartId !== null}
                            className="text-sm py-1 px-2"
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-6 flex justify-center">
                <ButtonWithLoading
                  type="button"
                  onClick={onClose}
                  text={tg('cancel')}
                  outline
                  className="px-4 py-2"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}));

export default LoadCartModal;